import {
  clamp,
  clamp01,
  cross,
  dist,
  dot,
  len,
  mid,
  norm,
  smoothstep,
  sub,
  v3,
  type Vec3,
} from '../core/math';
import { CalibrationCollector, emptyCalibration, type Calibration } from './calibration';
import { LANDMARK_COUNT, LM, type Side } from './landmarks';
import type { ArmState, BodyFrame, PoseFrame, RawPose } from './types';

/**
 * Turns a raw landmark set into the normalised feature vector everything else
 * consumes. This is the boundary between "computer vision" and "game": nothing
 * downstream of here knows what a landmark index is.
 */
export class PoseProcessor {
  calibration: Calibration = emptyCalibration();
  readonly collector = new CalibrationCollector();

  private body: Vec3[] = Array.from({ length: LANDMARK_COUNT }, () => v3());
  private prev: PoseFrame | null = null;
  private frameOut: BodyFrame = {
    origin: v3(),
    right: v3(1, 0, 0),
    up: v3(0, 1, 0),
    forward: v3(0, 0, 1),
    scale: 0.4,
    yaw: 0,
    pitch: 0,
  };

  /** Median-of-recent shoulder width, so one bad frame cannot rescale the world. */
  private swHistory: number[] = [];

  reset(): void {
    this.prev = null;
    this.swHistory.length = 0;
  }

  /**
   * Builds the torso basis.
   *
   * Deriving `up` from the pelvis->sternum vector rather than from world-Y is the
   * key trick: it means a player who leans, crouches or rolls does not smear
   * their lean into every other feature. Slips become head-relative-to-torso
   * instead of head-relative-to-the-room, which is what a coach actually means.
   */
  private buildFrame(world: Vec3[]): BodyFrame {
    const ls = world[LM.LEFT_SHOULDER];
    const rs = world[LM.RIGHT_SHOULDER];
    const lh = world[LM.LEFT_HIP];
    const rh = world[LM.RIGHT_HIP];
    const midShoulder = mid(ls, rs);
    const midHip = mid(lh, rh);

    // Subject's right, from left shoulder to right shoulder.
    let right = norm(sub(rs, ls));
    let up = norm(sub(midShoulder, midHip));
    // Gram-Schmidt: keep `up` authoritative (torsion of the shoulder line is
    // noisier than the spine) and re-orthogonalise `right` against it.
    right = norm(sub(right, scaleV(up, dot(right, up))));
    const forward = norm(cross(up, right));

    // Raw shoulder width is jittery; a short running median stabilises the unit
    // of measurement without adding lag to anything that matters.
    const rawSw = dist(ls, rs);
    this.swHistory.push(rawSw);
    if (this.swHistory.length > 31) this.swHistory.shift();
    const sorted = [...this.swHistory].sort((a, b) => a - b);
    const scale = Math.max(0.05, sorted[sorted.length >> 1]);

    // Yaw: how far the chest is turned away from the camera axis. The camera
    // looks down world -Z (MediaPipe world space is x-right, y-down, z-toward-viewer-negative).
    const camAxis = v3(0, 0, -1);
    const fwdFlat = norm(v3(forward.x, 0, forward.z));
    const yaw = (Math.atan2(fwdFlat.x, -fwdFlat.z) * 180) / Math.PI;
    const pitch = (Math.acos(clamp(dot(up, v3(0, -1, 0)), -1, 1)) * 180) / Math.PI;
    void camAxis;

    this.frameOut.origin = midHip;
    this.frameOut.right = right;
    this.frameOut.up = up;
    this.frameOut.forward = forward;
    this.frameOut.scale = scale;
    this.frameOut.yaw = yaw;
    this.frameOut.pitch = pitch;
    return this.frameOut;
  }

  private project(world: Vec3[], f: BodyFrame): void {
    const inv = 1 / f.scale;
    for (let i = 0; i < LANDMARK_COUNT; i++) {
      const d = sub(world[i], f.origin);
      const out = this.body[i];
      out.x = dot(d, f.right) * inv;
      out.y = dot(d, f.up) * inv;
      out.z = dot(d, f.forward) * inv;
    }
  }

  /**
   * Feed a raw pose while in calibration mode. Returns progress in [0,1].
   *
   * The acceptance gate is 0.6, not 0.8. Calibration only needs the torso, the
   * arms and the head to be measurable; demanding near-perfect quality meant a
   * player sitting slightly too close, or in a room the model found marginally
   * hard, could never start at all — a dead end with no way out of it.
   */
  calibrate(raw: RawPose): number {
    const f = this.buildFrame(raw.world);
    this.project(raw.world, f);
    if (raw.quality >= 0.6) this.collector.add(raw.world, raw.image, this.body, f);
    return clamp01(this.collector.count / 45);
  }

  /** Force-accept the current pose, for the "calibrate anyway" escape hatch. */
  forceSample(raw: RawPose): void {
    const f = this.buildFrame(raw.world);
    this.project(raw.world, f);
    this.collector.add(raw.world, raw.image, this.body, f);
  }

  finishCalibration(): Calibration {
    this.calibration = this.collector.finish();
    this.collector.reset();
    return this.calibration;
  }

  /** Main path: raw landmarks -> game-ready features. */
  process(raw: RawPose): PoseFrame {
    const c = this.calibration;
    const f = this.buildFrame(raw.world);
    this.project(raw.world, f);
    const b = this.body;
    const dt = this.prev ? Math.max(1e-3, (raw.t - this.prev.t) / 1000) : 1 / 30;

    const head = mid(b[LM.LEFT_EAR], b[LM.RIGHT_EAR]);
    const headOffset = sub(head, c.headHome);
    const headVel = this.prev ? scaleV(sub(head, this.prev.head), 1 / dt) : v3();

    // --- Ring-plane localisation from monocular cues -------------------------
    // Lateral: a displacement of dx in image-width units at the subject's depth
    // equals dx / imageShoulderWidth shoulder widths. No FOV assumption needed.
    // Depth: apparent size is inversely proportional to distance, so
    // distance_sw = focal / imageShoulderWidth. That one *does* assume an FOV,
    // which is why depth is treated as a soft cue and never as ground truth.
    const imgSw = Math.max(0.02, Math.abs(raw.image[LM.LEFT_SHOULDER].x - raw.image[LM.RIGHT_SHOULDER].x));
    const hipX = (raw.image[LM.LEFT_HIP].x + raw.image[LM.RIGHT_HIP].x) * 0.5;
    // Image x grows to the viewer's right, which is the subject's left. Negate.
    const ringX = -(hipX - c.imageHipX) / imgSw;
    const ringZ = c.homeDistance - c.focal / imgSw;
    const ringVel = this.prev
      ? { x: (ringX - this.prev.ring.x) / dt, z: (ringZ - this.prev.ring.z) / dt }
      : { x: 0, z: 0 };

    const arms = {
      left: this.arm('left', b, dt),
      right: this.arm('right', b, dt),
    };

    const footStagger = b[LM.LEFT_ANKLE].z - b[LM.RIGHT_ANKLE].z;
    const crouch = c.headHome.y - head.y;

    // --- Lean-back ----------------------------------------------------------
    // MediaPipe world z is negative toward the camera, so a spine tipping away
    // from the camera raises `up.z`. Multiply by the head's height above the hips
    // to convert that tilt into the distance the head actually travelled, then
    // add any head-on-neck retraction (which body-frame z does capture, since
    // that IS relative to the torso).
    const leanBack = f.up.z - c.restLeanZ;
    const pullDepth = HEAD_HEIGHT_SW * leanBack - headOffset.z;
    const pullRate = this.prev ? (pullDepth - this.prev.pullDepth) / dt : 0;

    const frame: PoseFrame = {
      t: raw.t,
      dt,
      quality: raw.quality,
      // Snapshot the basis for the same aliasing reason as the arm vectors.
      frame: { ...f },
      b,
      image: raw.image,
      vis: raw.vis,
      arms,
      head,
      headOffset,
      headVel,
      ring: { x: ringX, z: ringZ },
      ringVel,
      stance: c.stance,
      footStagger,
      crouch,
      pullDepth,
      pullRate,
      leanBack,
    };
    this.prev = frame;
    return frame;
  }

  private arm(side: Side, b: Vec3[], dt: number): ArmState {
    const S = side === 'left' ? LM.LEFT_SHOULDER : LM.RIGHT_SHOULDER;
    const E = side === 'left' ? LM.LEFT_ELBOW : LM.RIGHT_ELBOW;
    const W = side === 'left' ? LM.LEFT_WRIST : LM.RIGHT_WRIST;
    // These MUST be copies. `this.body` is a reused buffer that is overwritten in
    // place every frame; aliasing it into the frame would make `prev.arms.wrist`
    // identical to the current wrist and silently zero every velocity.
    const shoulder = { ...b[S] };
    const elbow = { ...b[E] };
    const wrist = { ...b[W] };

    const armLen = Math.max(0.6, this.calibration.armLength[side]);
    const reach = dist(shoulder, wrist);
    const extension = clamp(reach / armLen, 0, 1.2);

    const prevArm = this.prev?.arms[side];
    const vel = prevArm ? scaleV(sub(wrist, prevArm.wrist), 1 / dt) : v3();
    const extensionRate = prevArm ? (extension - prevArm.extension) / dt : 0;

    const u = norm(sub(shoulder, elbow));
    const w = norm(sub(wrist, elbow));
    const elbowAngle = (Math.acos(clamp(dot(u, w), -1, 1)) * 180) / Math.PI;

    // Guard quality: 1 when the glove sits where it sat during calibration,
    // falling off over ~0.55 sw. Deliberately generous — punishing small drift
    // would make every player feel permanently out of position.
    const home = this.calibration.guardHome[side];
    const guard = 1 - smoothstep(0.18, 0.62, dist(wrist, home));

    return { wrist, elbow, shoulder, extension, extensionRate, elbowAngle, vel, speed: len(vel), guard };
  }
}

/** Head height above the hip joint, in shoulder widths. Converts spine tilt to head travel. */
const HEAD_HEIGHT_SW = 1.6;

function scaleV(a: Vec3, s: number): Vec3 {
  return { x: a.x * s, y: a.y * s, z: a.z * s };
}
